import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { NotificationDisplayType } from '@this+that/models';
import log from 'loglevel';
import { LuBell, LuMail, LuTriangleAlert } from 'react-icons/lu';

import { PERMISSION_STATE, usePushNotifications } from './usePushNotifications';
import {
  ALERT_FAILURE_KINDS,
  ALERT_TITLE_KEY_BY_KIND,
  DEFAULT_ALERT_TITLE_KEY,
} from '../components/Notification/constants';
import { useTeamHub } from '../pages/private/Team/hooks/useTeamHub';
import { useMarkNotificationAsRead } from '../services/notification.service';
import { useNotificationsStore } from '../store';
import { openNotificationRedirect } from '../utils/openNotificationRedirect';

const PUSH_PROMPT_DELAY_MS = 5000;
const PUSH_DISMISSED_KEY = 'push_notification_prompt_dismissed';

export interface AlertQueueItem {
  icon: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  onDismiss?: () => void;
  dismissLabel?: string;
  actionLoading?: boolean;
}

function useTeamInviteAlertState() {
  const { pendingInvites, acceptInvite, declineInvite, acceptingInviteIds } =
    useTeamHub({
      autoLoad: false,
    });
  const invite = pendingInvites[0] ?? null;

  const handleAccept = useCallback(() => {
    if (!invite) {
      return;
    }
    acceptInvite(invite);
  }, [invite, acceptInvite]);

  const handleDecline = useCallback(() => {
    if (!invite) {
      return;
    }
    declineInvite(invite);
  }, [invite, declineInvite]);

  const isActive = invite !== null;
  const isAccepting = invite ? !!acceptingInviteIds[invite.teamId] : false;

  return { isActive, invite, handleAccept, handleDecline, isAccepting };
}

function useNotificationAlertState() {
  const notifications = useNotificationsStore((state) => state.notifications);
  const markAsRead = useNotificationsStore((state) => state.markAsRead);
  const { markNotificationAsRead } = useMarkNotificationAsRead();
  const navigate = useNavigate();

  const latestNotification = useMemo(() => {
    const unreadBanners = notifications.filter(
      (n) =>
        n.displayType === NotificationDisplayType.BANNER && n.wasRead === false,
    );
    if (unreadBanners.length === 0) {
      return null;
    }
    return [...unreadBanners].sort(
      (a, b) => (b.createdAt || 0) - (a.createdAt || 0),
    )[0];
  }, [notifications]);

  const handleClick = useCallback(async () => {
    if (!latestNotification?.id) {
      return;
    }
    // First and in its OWN try. First because transient user activation is gone once a
    // network round trip resolves, and `window.open` is then blocked silently. Isolated
    // because a SecurityError from an embedded webview must not skip the read marking below
    // — that would leave a CRITICAL banner permanently unread with a button that cannot
    // clear it.
    if (latestNotification.redirectUrl) {
      try {
        openNotificationRedirect(latestNotification.redirectUrl, navigate);
      } catch (error) {
        log.error('Failed to open the notification redirect:', error);
      }
    }

    try {
      markAsRead(latestNotification.id);
      await markNotificationAsRead(latestNotification.id);
    } catch (error) {
      log.error('Failed to handle notification click:', error);
    }
  }, [latestNotification, markAsRead, markNotificationAsRead, navigate]);

  const handleClose = useCallback(async () => {
    if (!latestNotification?.id) {
      return;
    }
    try {
      markAsRead(latestNotification.id);
      await markNotificationAsRead(latestNotification.id);
    } catch (error) {
      log.error('Failed to mark notification as read:', error);
    }
  }, [latestNotification, markAsRead, markNotificationAsRead]);

  return {
    isActive: latestNotification !== null,
    latestNotification,
    handleClick,
    handleClose,
  };
}

function usePushAlertState() {
  const { permissionState, subscribe } = usePushNotifications();
  const [delayComplete, setDelayComplete] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(PUSH_DISMISSED_KEY) !== null;
  });

  useEffect(() => {
    if (permissionState !== PERMISSION_STATE.PROMPT) {
      return;
    }
    const timer = setTimeout(
      () => setDelayComplete(true),
      PUSH_PROMPT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [permissionState]);

  const handleEnable = useCallback(async () => {
    await subscribe();
    setDelayComplete(false);
  }, [subscribe]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(PUSH_DISMISSED_KEY, 'true');
  }, []);

  const isActive =
    permissionState === PERMISSION_STATE.PROMPT && delayComplete && !dismissed;

  return { isActive, handleEnable, handleDismiss };
}

export function useAlertQueue(): { currentAlert: AlertQueueItem | null } {
  const { t } = useTranslation();

  const {
    isActive: inviteIsActive,
    invite,
    handleAccept: inviteHandleAccept,
    handleDecline: inviteHandleDecline,
    isAccepting: inviteIsAccepting,
  } = useTeamInviteAlertState();

  const {
    isActive: notificationIsActive,
    latestNotification,
    handleClick: notificationHandleClick,
    handleClose: notificationHandleClose,
  } = useNotificationAlertState();

  const {
    isActive: pushIsActive,
    handleEnable: pushHandleEnable,
    handleDismiss: pushHandleDismiss,
  } = usePushAlertState();

  const inviteRoleLabel = invite?.inviteeRole
    ? t(`teamHub.inviteRoles.${invite.inviteeRole.toLowerCase()}.name`)
    : '';

  const inviteAlert = useMemo<AlertQueueItem>(
    () => ({
      icon: <LuMail />,
      title: t('components.gradientAlert.inviteBannerTitle', {
        team: invite?.teamName ?? '',
      }),
      description: t('teamHub.overview.inviteMeta', {
        inviter: invite?.inviterUserName ?? '',
        role: inviteRoleLabel,
        count: invite?.memberCount ?? 0,
      }),
      actionLabel: t('teamHub.overview.accept'),
      onAction: inviteHandleAccept,
      secondaryActionLabel: t('teamHub.overview.decline'),
      onSecondaryAction: inviteHandleDecline,
      actionLoading: inviteIsAccepting,
    }),
    [
      t,
      invite,
      inviteRoleLabel,
      inviteHandleAccept,
      inviteHandleDecline,
      inviteIsAccepting,
    ],
  );

  const notificationAlert = useMemo<AlertQueueItem>(() => {
    const kind = latestNotification?.kind;

    return {
      icon:
        kind && ALERT_FAILURE_KINDS.has(kind) ? (
          <LuTriangleAlert />
        ) : (
          <LuBell />
        ),
      title: t(
        (kind && ALERT_TITLE_KEY_BY_KIND[kind]) ?? DEFAULT_ALERT_TITLE_KEY,
      ),
      description: latestNotification?.message ?? '',
      actionLabel: latestNotification?.redirectUrl
        ? t('components.gradientAlert.view')
        : undefined,
      onAction: latestNotification?.redirectUrl
        ? notificationHandleClick
        : undefined,
      onDismiss: notificationHandleClose,
      dismissLabel: t('components.gradientAlert.dismissAlert'),
    };
  }, [t, latestNotification, notificationHandleClick, notificationHandleClose]);

  const pushAlert = useMemo<AlertQueueItem>(
    () => ({
      icon: <LuBell />,
      title: t('pushNotifications.permissionPromptTitle'),
      description: t('pushNotifications.permissionPromptDescription'),
      actionLabel: t('pushNotifications.enableButton'),
      onAction: pushHandleEnable,
      onDismiss: pushHandleDismiss,
      dismissLabel: t('pushNotifications.dismissButton'),
    }),
    [t, pushHandleEnable, pushHandleDismiss],
  );

  const currentAlert = useMemo<AlertQueueItem | null>(() => {
    if (inviteIsActive) {
      return inviteAlert;
    }
    if (notificationIsActive) {
      return notificationAlert;
    }
    if (pushIsActive) {
      return pushAlert;
    }
    return null;
  }, [
    inviteIsActive,
    notificationIsActive,
    pushIsActive,
    inviteAlert,
    notificationAlert,
    pushAlert,
  ]);

  return { currentAlert };
}
